import type { DateRangeState } from '../hooks/useDateRange';

const INPUT_CLASS =
    'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-sm text-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';

const INVALID_MESSAGE = 'The start date must be on or before the end date.';

export interface DateRangeFieldProps {
    /** State from `useDateRange`; the caller owns it so it can read from/to. */
    range: DateRangeState;
    label?: string;
    /** Helper line under the inputs, replaced by the error while invalid. */
    hint?: string;
    /** Prefixes the input ids - give each field on a page its own. */
    idPrefix?: string;
    className?: string;
}

export function DateRangeField({
    range,
    label = 'Date range',
    hint,
    idPrefix = 'date-range',
    className = ''
}: DateRangeFieldProps) {
    const { from, to, setFrom, setTo, presets, activePresetLabel, applyPreset, isInvalid } = range;
    const fromId = `${idPrefix}-from`;
    const toId = `${idPrefix}-to`;

    return (
        <div className={className}>
            <div className="flex items-center justify-between mb-1.5">
                <span className="block text-[13px] font-semibold text-gray-700">{label}</span>
                <div className="flex items-center gap-1">
                    {presets.map((preset) => (
                        <button
                            key={preset.label}
                            type="button"
                            onClick={() => applyPreset(preset.months)}
                            className={`px-2 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                                activePresetLabel === preset.label
                                    ? 'bg-gray-800 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label htmlFor={fromId} className="block text-[11px] text-gray-400 mb-1">
                        From
                    </label>
                    <input
                        id={fromId}
                        type="date"
                        className={INPUT_CLASS}
                        value={from}
                        max={to || undefined}
                        onChange={(e) => setFrom(e.target.value)}
                    />
                </div>
                <div>
                    <label htmlFor={toId} className="block text-[11px] text-gray-400 mb-1">
                        To
                    </label>
                    <input
                        id={toId}
                        type="date"
                        className={INPUT_CLASS}
                        value={to}
                        min={from || undefined}
                        onChange={(e) => setTo(e.target.value)}
                    />
                </div>
            </div>
            {(isInvalid || hint) && (
                <p className={`mt-1 text-[11px] ${isInvalid ? 'text-red-500' : 'text-gray-400'}`}>
                    {isInvalid ? INVALID_MESSAGE : hint}
                </p>
            )}
        </div>
    );
}
